/*
 * File: AppTheme.tsx
 * Project: a-a
 * File Created: Tuesday, 8th October 2024 8:29:50 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Wednesday, 15th October 2025 11:45:28 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */
import { Palette, TypographyVariantsOptions, createTheme } from '@mui/material/styles';
// import zeplinColor from './Color';
// import {
//   TypographyStyleOptions,
//   TypographyVariantsOptions,
// } from '@mui/material/styles/createTypography';
import zeplinColor from './Color';

// const LightMode = 'light';
// declare module '@mui/material/Typography' {
//   interface TypographyPropsVariantOverrides {
//     buttonLarge: true;
//     buttonMedium: true;
//   }
// }

interface ExtendedTypographyOptions extends TypographyVariantsOptions {
  buttonLarge: React.CSSProperties;
  buttonMedium: React.CSSProperties;
}

declare module '@mui/material/styles/createPalette' {
  interface PaletteOptions {
    customDisable?: {
      main: string;
    };
    customSecondary?: {
      main?: string;
    };
  }
}

// Update the Button's color options
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    customDisable: true;
    customSecondary: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
    responsiveBody3: true;
    responsiveLabel1: true;
    responsiveLabel2: true;
    responsiveBody2: true;
    responsiveH1: true;
    responsiveHeading1: true;
    responsiveHeading2: true;
    responsiveHeading3: true;
    responsiveHeading4: true;
    responsiveDisplay6: true;
    responsiveDisplay5: true;
    responsiveDisplay4: true;
    responsiveCaption: true;
    responsiveButtonLarge: true;
    responsiveButtonMedium: true;
    responsiveButtonSmall: true;
    heading1: true;
    heading2: true;
    heading3: true;
    heading4: true;
    buttonLarge: true;
    buttonMedium: true;
    buttonSmall: true;
    label1: true;
    label2: true;
    display4: true;
    display5: true;
    display6: true;
  }
}
interface ExtendedTypographyOptions extends TypographyVariantsOptions {
  body3: React.CSSProperties;
  responsiveBody3: React.CSSProperties;
  responsiveBody2: React.CSSProperties;
  responsiveLabel1: React.CSSProperties;
  responsiveLabel2: React.CSSProperties;
  responsiveH1: React.CSSProperties;
  responsiveHeading1: React.CSSProperties;
  responsiveHeading2: React.CSSProperties;
  responsiveHeading3: React.CSSProperties;
  responsiveHeading4: React.CSSProperties;
  responsiveDisplay6: React.CSSProperties;
  responsiveDisplay4: React.CSSProperties;
  responsiveDisplay5: React.CSSProperties;
  responsiveCaption: React.CSSProperties;
  responsiveButtonLarge: React.CSSProperties;
  responsiveButtonMedium: React.CSSProperties;
  responsiveButtonSmall: React.CSSProperties;
  heading1: React.CSSProperties;
  heading2: React.CSSProperties;
  heading3: React.CSSProperties;
  heading4: React.CSSProperties;
  buttonLarge: React.CSSProperties;
  buttonMedium: React.CSSProperties;
  buttonSmall: React.CSSProperties;
  label1: React.CSSProperties;
  label2: React.CSSProperties;
  display4: React.CSSProperties;
  display5: React.CSSProperties;
  display6: React.CSSProperties;
}

interface ExtendedTypographyOptions extends TypographyVariantsOptions {
  buttonLarge: React.CSSProperties;
  buttonMedium: React.CSSProperties;
  buttonSmall: React.CSSProperties;
  responsiveButtonLarge: React.CSSProperties;
  responsiveButtonMedium: React.CSSProperties;
  responsiveButtonSmall: React.CSSProperties;
}

const appTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1700, // Custom breakpoint at 1700px
    },
  },
  typography: {
    fontFamily: 'HelveticaNeue',
    textTransform: 'none',
    h1: {
      fontSize: 16,
      fontWeight: 550,
      textTransform: 'none',
    },
    responsiveH1: {
      fontSize: 16,
      fontWeight: 550,
      textTransform: 'none',
    },
    h2: {
      fontSize: 16,
      fontWeight: 400,
      textTransform: 'none',
    },

    h3: {
      fontSize: 32,
      fontWeight: 600,
      textTransform: 'none',
    },
    h4: {
      fontSize: 28,
      fontWeight: 300,
      textTransform: 'none',
    },
    h5: {
      fontSize: 22,
      fontWeight: 550,
      textTransform: 'none',
    },
    h6: {
      fontSize: 20,
      fontWeight: 600,
      textTransform: 'none',
    },
    heading1: {
      fontSize: '22px',
      fontWeight: 550,
      // fontWeight: 'medium',
    },
    responsiveHeading1: {
      fontSize: '22px',
      fontWeight: 550,
      // fontWeight: 'medium',
    },
    heading2: {
      fontSize: '20px',
      fontWeight: 550,
      // fontWeight: 'medium',
    },
    responsiveHeading2: {
      fontSize: '20px',
      fontWeight: 550,
      // fontWeight: 'medium',
    },
    responsiveHeading3: {
      fontSize: 18,
      fontWeight: 'bold',
    },
    heading3: {
      fontSize: 18,
      fontWeight: 'bold',
    },
    heading4: {
      fontSize: 16,
      fontWeight: 550,
    },
    responsiveHeading4: {
      fontSize: 16,
      fontWeight: 550,
    },
    display4: {
      fontSize: 28,
      fontWeight: 700,
    },
    responsiveDisplay4: {
      fontSize: 28,
      fontWeight: 700,
    },
    display5: {
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: '29.3px',
    },
    responsiveDisplay5: {
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: '29.3px',
    },
    display6: {
      fontSize: '20px',
      fontWeight: 700,
      lineHeight: '29.3px',
    },
    responsiveDisplay6: {
      fontSize: '20px',
      fontWeight: 700,
      lineHeight: '29.3px',
    },
    body1: {
      fontSize: 16,
      fontWeight: 400,
      textTransform: 'none',
    },
    body2: {
      //styleName: Body/Body 2;
      fontSize: 16,
      fontWeight: 400,
      lineHeight: '19.09px',
      letterSpacing: '0.005em',
      textTransform: 'none',
    },
    responsiveBody2: {
      //styleName: Body/Body 2;
      fontSize: 16,
      fontWeight: 400,
      lineHeight: '19.09px',
      letterSpacing: '0.005em',
      textTransform: 'none',
    },
    body3: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '16.7px',
      textTransform: 'none',
    },

    responsiveBody3: {
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '16.7px',
      textTransform: 'none',
    },
    responsiveLabel1: {
      fontWeight: 400,
      fontSize: 12,
      // lineHeight: '14.4px', // Adjust line height as needed
      // textTransform: 'none',
    },

    label1: {
      fontSize: 12,
      fontWeight: 400,
    },
    label2: {
      fontSize: 10,
      fontWeight: 400,
    },
    responsiveLabel2: {
      fontSize: 10,
      fontWeight: 400,
    },
    caption: {
      fontSize: 12,
      fontWeight: 400,
    },
    responsiveCaption: {
      fontSize: 12,
      fontWeight: 400,
    },
    buttonLarge: {
      fontSize: 15,
      fontWeight: 600,
      textTransform: 'none',
    },
    buttonMedium: {
      fontSize: 16,
      fontWeight: 500,
      textTransform: 'none',
    },
    responsiveButtonLarge: {
      fontSize: 15,
      fontWeight: 500,
      textTransform: 'none',
    },
    responsiveButtonMedium: {
      fontSize: 16,
      fontWeight: 500,
      textTransform: 'none',
    },

    buttonSmall: {
      fontSize: 14,
      fontWeight: 400,
      textTransform: 'none',
    },
    responsiveButtonSmall: {
      fontSize: 14,
      fontWeight: 400,
      textTransform: 'none',
    },
  } as ExtendedTypographyOptions,
  palette: {
    primary: {
      main: zeplinColor.Brand1,
    },
    secondary: {
      main: zeplinColor.Neutral9,
    },
    customDisable: {
      main: zeplinColor.Neutral5,
    },
    text: {
      primary: zeplinColor.BasicBlack,
      secondary: zeplinColor.Neutral8,
      //   main:zeplinColor.Neutral1
    },
    error: {
      main: zeplinColor.Error,
    },

    background: {
      default: zeplinColor.Neutral1,
      paper: zeplinColor.Neutral1,
    },

    customSecondary: {
      main: zeplinColor.Neutral1,
    },
  },
  components: {
    // Name of the component
    MuiTypography: {
      variants: [
        {
          props: { variant: 'responsiveButtonLarge' },
          style: ({ theme }) => ({
            fontSize: '12px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '15px', // For screens 1700px and above
            },
          }),
        },
        {
          props: { variant: 'responsiveButtonMedium' },
          style: ({ theme }) => ({
            fontSize: '12px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '16px', // For screens 1700px and above
            },
          }),
        },
        {
          props: { variant: 'responsiveButtonSmall' },
          style: ({ theme }) => ({
            fontSize: '12px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '14px', // For screens 1700px and above
            },
          }),
        },
        {
          props: { variant: 'responsiveDisplay6' },
          style: ({ theme }) => ({
            fontSize: '16px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '20px', // For screens 1700px and above
            },
          }),
        },
        {
          props: { variant: 'responsiveDisplay5' },
          style: ({ theme }) => ({
            fontSize: '18px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '24px', // For screens 1700px and above
            },
          }),
        },
        {
          props: { variant: 'responsiveDisplay4' },
          style: ({ theme }) => ({
            fontSize: '24px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '28px', // For screens 1700px and above
            },
          }),
        },
        {
          props: { variant: 'responsiveBody3' },
          style: ({ theme }) => ({
            fontSize: '14px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '14px', // For screens 1700px and above
            },
          }),
        },
        {
          props: { variant: 'responsiveHeading1' },
          style: ({ theme }) => ({
            fontSize: '18px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '22px', // For screens 1700px and above
            },
          }),
        },
        {
          props: { variant: 'responsiveHeading2' },
          style: ({ theme }) => ({
            fontSize: '16px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '20px', // For screens 1700px and above
            },
          }),
        },
        {
          props: { variant: 'responsiveHeading3' },
          style: ({ theme }) => ({
            fontSize: '16px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '18px', // For screens 1700px and above
            },
          }),
        },
        {
          props: { variant: 'responsiveHeading4' },
          style: ({ theme }) => ({
            fontSize: '14px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '16px', // For screens 1700px and above
            },
          }),
        },
        {
          props: { variant: 'responsiveLabel1' },
          style: ({ theme }) => ({
            fontSize: '12px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '12px', // For screens 1700px and above
            },
          }),
        },
        {
          props: { variant: 'responsiveLabel2' },
          style: ({ theme }) => ({
            fontSize: '6px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '10px', // For screens 1700px and above
            },
          }),
        },
        {
          props: { variant: 'responsiveBody2' },
          style: ({ theme }) => ({
            fontSize: '14px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '16px', // For screens 1700px and above
            },
          }),
        },
        {
          props: { variant: 'responsiveH1' },
          style: ({ theme }) => ({
            fontSize: '12px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '16px', // For screens 1700px and above
            },
          }),
        },
        {
          props: { variant: 'responsiveCaption' },
          style: ({ theme }) => ({
            fontSize: '12px', // Default for small screens (below xl breakpoint)
            [theme.breakpoints.up('xl')]: {
              fontSize: '12px', // For screens 1700px and above
            },
          }),
        },
      ],
    },
    MuiInputBase: {
      styleOverrides: {
        // Name of the slot
        root: {
          // Some CSS
          borderRadius: 14,
          '& .MuiOutlinedInput-notchedOutline:hover': {
            borderColor: zeplinColor.Neutral9, // Change default hover color
          },
          '& .Mui-focused.MuiOutlinedInput-notchedOutline': {
            borderColor: zeplinColor.Success, // Change default focus color
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: zeplinColor.Neutral9,
          '&.Mui-error': {
            color: zeplinColor.Error,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          // '&.Mui-checked': {
          //   color: zeplinColor.BasicBlack,
          // },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: zeplinColor.Neutral10,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        // Name of the slot

        root: ({ theme }) => ({
          borderRadius: 14, // Default for xs and below xl
          // fontSize: 16,
          [theme.breakpoints.down('xl')]: {
            borderRadius: 10, // 14px for xl and above
            // fontSize: 14,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: zeplinColor.Neutral9,
          },
          '&:hover:not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
            borderColor: zeplinColor.Neutral9,
          },
          '&.Mui-focused': {
            borderColor: zeplinColor.Success,
          },
        }),
      },
    },
  },
});

export default appTheme;
